// PDF Editor Types
export type RedactionTool = 'select' | 'blackout' | 'highlight' | 'box';

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Redaction {
  id: string;
  type: RedactionTool;
  page: number;
  bounds: BoundingBox;
  color?: string;
  opacity?: number;
  text?: string;
  createdAt: Date;
}

export interface TextBlock {
  text: string;
  bounds: BoundingBox;
  page: number;
  fontName?: string;
  fontSize?: number;
}

export interface PDFDocumentInfo {
  numPages: number;
  title?: string;
  author?: string;
  pageWidth: number;
  pageHeight: number;
}

export type ZoomLevel = 25 | 50 | 75 | 100 | 125 | 150 | 175 | 200;

export interface EditorCommand {
  id: string;
  type: 'add' | 'remove' | 'modify';
  redaction: Redaction;
  previousState?: Redaction;
  execute: () => void;
  undo: () => void;
}

export interface PDFViewerState {
  currentPage: number;
  zoom: number;
  zoomMode: ZoomLevel;
  activeTool: RedactionTool;
  redactions: Redaction[];
  selectedRedaction: string | null;
  isLoading: boolean;
  error: string | null;
}

// AI Entity Detection Types
// This should match the server's entity_types.py definitions
export type EntityType =
  // SpaCy NER types
  | 'PERSON'
  | 'ORG'
  | 'GPE'
  | 'LOC'
  | 'DATE'
  | 'TIME'
  | 'MONEY'
  | 'PERCENT'
  | 'CARDINAL'
  | 'ORDINAL'
  | 'QUANTITY'
  | 'EVENT'
  | 'FAC'
  | 'LANGUAGE'
  | 'LAW'
  | 'NORP'
  | 'PRODUCT'
  | 'WORK_OF_ART'
  // Regex pattern types
  | 'SSN'
  | 'CREDIT_CARD'
  | 'PHONE'
  | 'EMAIL'
  | 'BANK_ACCOUNT'
  | 'ADDRESS'
  | 'MEDICAL'
  // EntityRuler types (government IDs)
  | 'PASSPORT'
  | 'DRIVERS_LICENSE'
  | 'EIN'
  | 'ITIN'
  | 'MILITARY_ID'
  | 'CASE_NUMBER'
  | 'GOV_EMPLOYEE_ID'
  | 'MEDICARE_NUMBER'
  | 'VA_FILE_NUMBER'
  | 'STATE_ID'
  | 'VISA_NUMBER'
  | 'GREEN_CARD'
  // Dictionary match
  | 'DICTIONARY'
  // Custom
  | 'CUSTOM';

export type EngineSource = 'spacy' | 'presidio' | 'llm' | 'dictionary' | 'manual' | 'ai' | 'johnsnowlabs';

export const ENGINE_COLOR_CONFIG: Record<EngineSource, { color: string; label: string }> = {
  spacy: { color: '#3B82F6', label: 'spaCy' },
  presidio: { color: '#F97316', label: 'Presidio' },
  llm: { color: '#8B5CF6', label: 'LLM' },
  johnsnowlabs: { color: '#DC2626', label: 'JohnSnowLabs' },
  dictionary: { color: '#22C55E', label: 'Dictionary' },
  manual: { color: '#6B7280', label: 'Manual' },
  ai: { color: '#6B7280', label: 'AI (legacy)' },
};

export interface EntitySuggestion {
  index: number;
  entityType: EntityType;
  text: string;
  confidence: number;
  page: number;
  bounds?: BoundingBox;
  replacementText?: string;
  status: 'pending' | 'approved' | 'rejected';
  source?: EngineSource;
  dictionarySource?: 'global' | 'case' | 'file';
  dictionaryAction?: 'always_redact' | 'always_allow';
  manuallyOverridden?: boolean;
  startPos?: number;
  isOptimistic?: boolean;
}

// Word Dictionary Types
export interface WordDictionaryEntry {
  id: string;
  tenant_id: string;
  scope_type: 'global' | 'case' | 'file';
  scope_id?: string;
  word: string;
  match_type: 'exact' | 'contains';
  action: 'always_redact' | 'always_allow';
  reason?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface RedactionJob {
  jobId: string;
  documentId: string;
  status: 'pending' | 'processing' | 'awaiting_approval' | 'approved' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  entitiesDetected: number;
  entitiesApproved: number;
  entitiesRejected: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

// Entity type display configurations
export const ENTITY_TYPE_CONFIG: Record<EntityType, { label: string; color: string; icon: string }> = {
  // SpaCy NER types
  PERSON: { label: 'Name', color: '#EF4444', icon: 'user' },
  ORG: { label: 'Organization', color: '#14B8A6', icon: 'building' },
  GPE: { label: 'Location (GPE)', color: '#EC4899', icon: 'map-pin' },
  LOC: { label: 'Location', color: '#F472B6', icon: 'map-pin' },
  DATE: { label: 'Date', color: '#6366F1', icon: 'calendar' },
  TIME: { label: 'Time', color: '#818CF8', icon: 'clock' },
  MONEY: { label: 'Money', color: '#22C55E', icon: 'dollar-sign' },
  PERCENT: { label: 'Percent', color: '#84CC16', icon: 'percent' },
  CARDINAL: { label: 'Number', color: '#A3A3A3', icon: 'hash' },
  ORDINAL: { label: 'Ordinal', color: '#D4D4D4', icon: 'list-ordered' },
  QUANTITY: { label: 'Quantity', color: '#FBBF24', icon: 'scale' },
  EVENT: { label: 'Event', color: '#F97316', icon: 'calendar-days' },
  FAC: { label: 'Facility', color: '#FB923C', icon: 'building-2' },
  LANGUAGE: { label: 'Language', color: '#A78BFA', icon: 'globe' },
  LAW: { label: 'Law', color: '#C084FC', icon: 'scale' },
  NORP: { label: 'Group', color: '#E879F9', icon: 'users' },
  PRODUCT: { label: 'Product', color: '#F0ABFC', icon: 'package' },
  WORK_OF_ART: { label: 'Work of Art', color: '#D946EF', icon: 'palette' },

  // Regex pattern types
  SSN: { label: 'SSN', color: '#F59E0B', icon: 'shield' },
  CREDIT_CARD: { label: 'Credit Card', color: '#8B5CF6', icon: 'credit-card' },
  PHONE: { label: 'Phone', color: '#10B981', icon: 'phone' },
  EMAIL: { label: 'Email', color: '#3B82F6', icon: 'mail' },
  BANK_ACCOUNT: { label: 'Bank Account', color: '#059669', icon: 'landmark' },
  ADDRESS: { label: 'Address', color: '#EC4899', icon: 'map-pin' },
  MEDICAL: { label: 'Medical', color: '#DC2626', icon: 'heart-pulse' },

  // EntityRuler types (government IDs)
  PASSPORT: { label: 'Passport', color: '#7C3AED', icon: 'id-card' },
  DRIVERS_LICENSE: { label: 'Driver License', color: '#0891B2', icon: 'id-card' },
  EIN: { label: 'EIN', color: '#0D9488', icon: 'file-text' },
  ITIN: { label: 'ITIN', color: '#14B8A6', icon: 'file-text' },
  MILITARY_ID: { label: 'Military ID', color: '#065F46', icon: 'shield' },
  CASE_NUMBER: { label: 'Case Number', color: '#7C2D12', icon: 'folder' },
  GOV_EMPLOYEE_ID: { label: 'Gov Employee ID', color: '#1E40AF', icon: 'badge' },
  MEDICARE_NUMBER: { label: 'Medicare', color: '#1D4ED8', icon: 'heart' },
  VA_FILE_NUMBER: { label: 'VA File Number', color: '#1E3A8A', icon: 'file' },
  STATE_ID: { label: 'State ID', color: '#3730A3', icon: 'id-card' },
  VISA_NUMBER: { label: 'Visa Number', color: '#5B21B6', icon: 'plane' },
  GREEN_CARD: { label: 'Green Card', color: '#166534', icon: 'id-card' },

  // Dictionary match
  DICTIONARY: { label: 'Dictionary', color: '#F97316', icon: 'book' },

  // Custom
  CUSTOM: { label: 'Custom', color: '#64748B', icon: 'tag' },
};

export function getHighlightColor(
  suggestion: EntitySuggestion,
  isTesterMode: boolean,
): string {
  if (!isTesterMode) {
    const config = ENTITY_TYPE_CONFIG[suggestion.entityType] || ENTITY_TYPE_CONFIG.CUSTOM;
    return config.color;
  }
  const source = (suggestion.source || 'ai') as EngineSource;
  return ENGINE_COLOR_CONFIG[source]?.color || ENGINE_COLOR_CONFIG.ai.color;
}

// Case Management Types
export type CaseStatus = 'not_started' | 'in_progress' | 'completed';
export type DocumentStatus = 'unprocessed' | 'needs_redaction' | 'redacted' | 'completed';

export interface Case {
  id: string;
  name: string;
  status: CaseStatus;
  notes?: string;
  owner: string;
  owner_name?: string;
  tenant_id: string;
  document_count: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  metadata?: Record<string, unknown>;
}

export interface CaseDocument {
  id: string;
  name: string;
  type: string;
  upload_date: string;
  status: DocumentStatus;
  file_size: number;
  page_count?: number;
  case_id?: string;
  parent_id?: string;           // For child documents (extracted from PST/email)
  children?: CaseDocument[];    // For tree structure in UI
  children_count?: number;      // Count of direct children
  is_container?: boolean;       // True for PST files that are containers only
}

export interface CaseWithDocuments extends Case {
  documents: CaseDocument[];
}

export interface CaseStatistics {
  total_documents: number;
  unprocessed: number;
  needs_redaction: number;
  redacted: number;
  completed: number;
  case_status: CaseStatus;
}

// Case status display configuration
export const CASE_STATUS_CONFIG: Record<CaseStatus, { label: string; color: string; bgColor: string }> = {
  not_started: { label: 'Not Started', color: '#6B7280', bgColor: '#F3F4F6' },
  in_progress: { label: 'In Progress', color: '#3B82F6', bgColor: '#DBEAFE' },
  completed: { label: 'Completed', color: '#10B981', bgColor: '#D1FAE5' },
};

// Document status display configuration
export const DOCUMENT_STATUS_CONFIG: Record<DocumentStatus, { label: string; color: string; bgColor: string }> = {
  unprocessed: { label: 'Unprocessed', color: '#EF4444', bgColor: '#FEE2E2' },
  needs_redaction: { label: 'Needs Redaction', color: '#F59E0B', bgColor: '#FEF3C7' },
  redacted: { label: 'Redacted', color: '#3B82F6', bgColor: '#DBEAFE' },
  completed: { label: 'Completed', color: '#10B981', bgColor: '#D1FAE5' },
};

// Redaction Reason Types
export interface RedactionReason {
  id: string;
  title: string;
  description: string;
  tenant_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// PST (Outlook Personal Storage Table) Types
// =============================================================================

// PST Attachment metadata
export interface PSTAttachment {
  attachment_id: string;
  message_id: string;
  filename: string;
  content_type?: string;
  size_bytes: number;
}

// PST Message header (for lazy loading - minimal data for list view)
export interface PSTMessageHeader {
  message_id: string;
  message_index: number;  // 0-indexed position for navigation
  folder_path: string;
  subject?: string;
  from: string;           // Format: "Display Name" <email@domain.com>
  to: string;             // Format: "Display Name" <email@domain.com>
  cc?: string;
  date?: string;
  has_attachments: boolean;
  attachment_count: number;
}

// PST Message full content (loaded on demand)
export interface PSTMessage {
  message_id: string;
  message_index: number;
  subject?: string;
  from: string;           // Format: "Display Name" <email@domain.com>
  to: string;
  cc?: string;
  date?: string;
  html_body?: string;
  plain_body?: string;
  attachments: PSTAttachment[];
}

// PST Folder metadata
export interface PSTFolder {
  folder_id: string;
  name: string;
  path: string;
  parent_id?: string;
  message_count: number;
  subfolder_count: number;
}

// PST Structure overview (for initial navigation)
export interface PSTStructure {
  document_id: string;
  total_messages: number;
  total_folders: number;
  total_attachments: number;
  folders: PSTFolder[];
}

// PST Message list response (paginated)
export interface PSTMessageList {
  document_id: string;
  messages: PSTMessageHeader[];
  total: number;
  offset: number;
  limit: number;
}

// Extended EntitySuggestion with message index for PST files
export interface PSTEntitySuggestion extends EntitySuggestion {
  messageIndex: number;   // Which message this suggestion belongs to
  fieldType?: string;     // 'from', 'to', 'cc', 'subject', 'body', 'attachment'
}

// PST Entity location for batch redaction
export interface PSTEntityLocation {
  entity_type: string;
  text: string;
  confidence: number;
  message_id: string;
  message_index: number;
  field_type: string;
  attachment_id?: string;
  replacement_text?: string;
}

// PST Batch redaction response
export interface PSTBatchRedactionResponse {
  document_id: string;
  redacted_document_id?: string;
  messages_processed: number;
  entities_redacted: number;
  status: string;
  message: string;
}

// PST text extraction result (for entity detection)
export interface PSTExtractionResult {
  document_id: string;
  total_messages: number;
  messages_processed: number;
  combined_text: string;
  message_text_ranges: Record<string, [number, number]>;
  message_index_map: Record<string, number>;
}
